import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { AlertCircle, CheckCircle, Download, Loader2, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { supermetricsService, DATE_RANGE_OPTIONS } from '../utils/supermetrics.js';

interface IngestionResult {
  successful: Array<{
    success: boolean;
    dateRange: string;
    count: number;
  }>;
  failed: Array<{
    success: boolean;
    dateRange: string;
    error: string;
  }>;
  totalCount: number;
}

export function SupermetricsIngestion({ onDataIngested }: { onDataIngested?: () => void }) {
  const [apiKey, setApiKey] = useState('');
  const [selectedRanges, setSelectedRanges] = useState(['last_week_sun_sat']);
  const [isIngesting, setIsIngesting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<IngestionResult | null>(null);
  const [currentRange, setCurrentRange] = useState('');

  const dateRangeOptions = supermetricsService.getDateRangeOptions();

  const handleRangeToggle = (range: string) => {
    setSelectedRanges(prev => 
      prev.includes(range) 
        ? prev.filter(r => r !== range)
        : [...prev, range]
    );
  };

  const handleSelectAll = () => {
    setSelectedRanges(DATE_RANGE_OPTIONS.slice());
  };

  const handleSelectNone = () => {
    setSelectedRanges([]);
  };

  const handleIngest = async () => {
    if (!apiKey.trim()) {
      console.error('[ERROR] API Key required: Please enter your API key');
      return;
    }

    if (selectedRanges.length === 0) {
      console.error('[ERROR] No date ranges selected: Please select at least one date range to ingest');
      return;
    }

    setIsIngesting(true);
    setProgress(0);
    setResults(null);
    setCurrentRange('');

    try {
      // Process ranges sequentially to show progress
      const allResults = { successful: [], failed: [], totalCount: 0 };
      
      for (let i = 0; i < selectedRanges.length; i++) {
        const range = selectedRanges[i];
        setCurrentRange(range);
        setProgress((i / selectedRanges.length) * 100);

        const result = await supermetricsService.ingestDateRange(range, apiKey.trim());
        
        if (result.success) {
          allResults.successful.push(result);
          allResults.totalCount += result.count;
        } else {
          allResults.failed.push(result);
        }
      }

      setProgress(100);
      setCurrentRange('');
      setResults(allResults);

      // Show summary log
      if (allResults.failed.length === 0) {
        console.log('[SUCCESS] Data ingestion completed: Successfully imported', allResults.totalCount.toLocaleString(), 'records across', allResults.successful.length, 'date ranges');
      } else {
        console.warn('[WARNING] Data ingestion completed with errors: Imported', allResults.totalCount.toLocaleString(), 'records.', allResults.failed.length, 'ranges failed.');
      }

      // Trigger refresh of main dashboard
      if (onDataIngested) {
        onDataIngested();
      }

    } catch (error) {
      console.error('[ERROR] Ingestion failed:', error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsIngesting(false);
      setProgress(0);
      setCurrentRange('');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Data Ingestion
            </CardTitle>
            <CardDescription>
              Import Facebook Ads data
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* API Key Input */}
        <div className="space-y-2">
          <Label htmlFor="api-key">API Key</Label>
          <Input
            id="api-key"
            type="password"
            placeholder="Enter your API key..."
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={isIngesting}
          />
        </div>

        {/* Date Range Selection */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label>Date Ranges to Import</Label>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSelectAll}
                disabled={isIngesting}
              >
                Select All
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSelectNone}
                disabled={isIngesting}
              >
                Clear
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {dateRangeOptions.map((option) => (
              <Label
                key={option.value}
                className="flex items-center space-x-2 cursor-pointer p-2 rounded border hover:bg-muted/50"
              >
                <input
                  type="checkbox"
                  checked={selectedRanges.includes(option.value)}
                  onChange={() => handleRangeToggle(option.value)}
                  disabled={isIngesting}
                  className="rounded"
                />
                <span className="text-sm">{option.label}</span>
              </Label>
            ))}
          </div>
          
          <div className="text-sm text-muted-foreground">
            Selected: {selectedRanges.length} range{selectedRanges.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Progress */}
        {isIngesting && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Importing data...</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} />
            {currentRange && (
              <div className="text-sm text-muted-foreground">
                Processing: {supermetricsService.formatDateRangeLabel(currentRange)}
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Import Results</span>
            </div>
            
            {results.successful.length > 0 && (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Successfully imported <strong>{results.totalCount.toLocaleString()}</strong> records 
                  across <strong>{results.successful.length}</strong> date range{results.successful.length !== 1 ? 's' : ''}
                </AlertDescription>
              </Alert>
            )}

            {results.failed.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>{results.failed.length}</strong> date range{results.failed.length !== 1 ? 's' : ''} failed to import:
                  <ul className="mt-2 list-disc list-inside">
                    {results.failed.map((failure, index) => (
                      <li key={index} className="text-xs">
                        {supermetricsService.formatDateRangeLabel(failure.dateRange)}: {failure.error}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {results.successful.map((success, index) => (
                <Badge key={index} variant="secondary" className="justify-between">
                  <span className="truncate">{supermetricsService.formatDateRangeLabel(success.dateRange)}</span>
                  <span>{success.count}</span>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Action Button */}
        <Button 
          onClick={handleIngest}
          disabled={isIngesting || !apiKey.trim() || selectedRanges.length === 0}
          className="w-full"
        >
          {isIngesting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Importing Data...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Import Data
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}